// ── Project API client ───────────────────────────────────────────
// Thin wrapper around the Digital Products API Project module
// (see http://localhost:8400 → Project).

import { request, requestWithMeta } from "@/lib/api/client";
import {
  cachedRequest,
  invalidateGenerationsAndProjects,
  invalidateProjects,
  LIST_TTL_MS,
  PROJECTS_CACHE_PREFIX,
} from "@/lib/api/cache";
import {
  castToolRunModality,
  formatGenerationDate,
  generationItemTitle,
  isGenerationToday,
} from "@/lib/api/generations";
import type { Project, ProjectItem } from "@/lib/types";

/**
 * Slim generation row embedded in GET /v1/project list items
 * (`ProjectGenerationSummary`).
 */
export interface ApiProjectGenerationSummary {
  id: number;
  slug: string;
  /**
   * User-provided generation title from the run request (`title` in the API).
   * Null for legacy rows — UI falls back to toolTitle / slug.
   */
  title?: string | null;
  toolTitle: string;
  modality: string;
  createdAt: string;
}

export interface ApiProject {
  id: number;
  slug: string;
  name: string;
  description?: string | null;
  notes?: string | null;
  /** Total generations linked to this project (may exceed `generations.length`). */
  generationCount?: number;
  /** Up to 5 most recent generation summaries for the project card. */
  generations?: ApiProjectGenerationSummary[];
  createdAt: string;
  updatedAt: string;
}

/** One project from GET /v1/project/{slug} (`ProjectDetailData`). */
export interface ApiProjectDetail {
  id: number;
  slug: string;
  name: string;
  description?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FetchProjectParams {
  /** Preferred locale for localized fields (`Accept-Language`). */
  locale?: string;
}

export interface FetchProjectGenerationsParams {
  page?: number;
  perPage?: number;
  /** Preferred locale for tool titles (`Accept-Language`). */
  locale?: string;
}

export interface ApiPagination {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

export interface ApiListMeta {
  requestId: string;
  pagination?: ApiPagination | null;
}

export interface FetchProjectsParams {
  page?: number;
  perPage?: number;
  /** Preferred locale for localized fields (`Accept-Language`). */
  locale?: string;
}

export interface ProjectWriteBody {
  name: string;
  description?: string;
  notes?: string;
}

export interface ProjectPatchBody {
  name?: string;
  description?: string;
  notes?: string;
}

/** GET /v1/project — paginated list for the current user. */
export function fetchProjects(
  params: FetchProjectsParams = {},
): Promise<{ data: ApiProject[]; meta: ApiListMeta }> {
  const qs = new URLSearchParams();
  if (params.page != null) qs.set("page", String(params.page));
  if (params.perPage != null) qs.set("perPage", String(params.perPage));
  const query = qs.toString();
  // Cached per page/perPage/locale. Every project write invalidates the
  // namespace, so paging back and forth costs nothing but a stale list is
  // never shown after a create/edit/notes save.
  const key = `${PROJECTS_CACHE_PREFIX}${params.page ?? 1}:${
    params.perPage ?? ""
  }:${params.locale ?? ""}`;
  return cachedRequest(
    key,
    () =>
      requestWithMeta<ApiProject[], ApiListMeta>(
        `/v1/project${query ? `?${query}` : ""}`,
        {
          method: "GET",
          headers: {
            ...(params.locale ? { "Accept-Language": params.locale } : {}),
          },
        },
      ),
    LIST_TTL_MS,
  );
}

/** Dedupes concurrent GET /v1/project/{slug} (e.g. React Strict Mode). */
const projectShowInflight = new Map<string, Promise<ApiProjectDetail>>();

/** GET /v1/project/{slug} — one project (no embedded generations). */
export function fetchProject(
  slug: string,
  params: FetchProjectParams = {},
): Promise<ApiProjectDetail> {
  const path = `/v1/project/${encodeURIComponent(slug)}`;
  const inflightKey = `${path}\0${params.locale ?? ""}`;

  const existing = projectShowInflight.get(inflightKey);
  if (existing) return existing;

  const promise = request<ApiProjectDetail>(path, {
    method: "GET",
    headers: {
      ...(params.locale ? { "Accept-Language": params.locale } : {}),
    },
  }).finally(() => {
    if (projectShowInflight.get(inflightKey) === promise) {
      projectShowInflight.delete(inflightKey);
    }
  });

  projectShowInflight.set(inflightKey, promise);
  return promise;
}

/** Dedupes concurrent GET /v1/project/{slug}/generation for the same query. */
const projectGenerationsInflight = new Map<
  string,
  Promise<{ data: ApiProjectGenerationSummary[]; meta: ApiListMeta }>
>();

/** GET /v1/project/{slug}/generation — paginated slim history for one project. */
export function fetchProjectGenerations(
  slug: string,
  params: FetchProjectGenerationsParams = {},
): Promise<{ data: ApiProjectGenerationSummary[]; meta: ApiListMeta }> {
  const qs = new URLSearchParams();
  if (params.page != null) qs.set("page", String(params.page));
  if (params.perPage != null) qs.set("perPage", String(params.perPage));
  const query = qs.toString();
  const path = `/v1/project/${encodeURIComponent(slug)}/generation${
    query ? `?${query}` : ""
  }`;
  const inflightKey = `${path}\0${params.locale ?? ""}`;

  const existing = projectGenerationsInflight.get(inflightKey);
  if (existing) return existing;

  const promise = requestWithMeta<ApiProjectGenerationSummary[], ApiListMeta>(
    path,
    {
      method: "GET",
      headers: {
        ...(params.locale ? { "Accept-Language": params.locale } : {}),
      },
    },
  ).finally(() => {
    if (projectGenerationsInflight.get(inflightKey) === promise) {
      projectGenerationsInflight.delete(inflightKey);
    }
  });

  projectGenerationsInflight.set(inflightKey, promise);
  return promise;
}

/** Narrow a detail payload to the list/catalog shape. */
export function toApiProjectListItem(
  detail: ApiProjectDetail,
  generationCount = 0,
): ApiProject {
  return {
    id: detail.id,
    slug: detail.slug,
    name: detail.name,
    description: detail.description,
    notes: detail.notes,
    generationCount,
    generations: [],
    createdAt: detail.createdAt,
    updatedAt: detail.updatedAt,
  };
}

/** Map a slim project-generation summary onto the UI item shape. */
export function mapApiProjectGenerationSummary(
  g: ApiProjectGenerationSummary,
  locale?: string,
): ProjectItem {
  const createdMs = new Date(g.createdAt).getTime();
  return {
    name: generationItemTitle(g),
    slug: g.slug,
    tool: g.toolTitle,
    date: formatGenerationDate(g.createdAt, locale),
    generated: isGenerationToday(g.createdAt),
    createdAt: Number.isNaN(createdMs) ? undefined : createdMs,
    modality: castToolRunModality(g.modality),
  };
}

/** Map GET /v1/project/{slug} onto the UI `Project` shape (items loaded separately). */
export function mapApiProjectDetail(detail: ApiProjectDetail): Project {
  return {
    id: String(detail.id),
    slug: detail.slug,
    name: detail.name,
    desc: detail.description ?? "",
    seed: false,
    items: [],
  };
}

/** POST /v1/project — create a project for the current user. */
export function storeProject(
  body: ProjectWriteBody,
  locale?: string,
): Promise<ApiProject> {
  return request<ApiProject>("/v1/project", {
    method: "POST",
    headers: {
      ...(locale ? { "Accept-Language": locale } : {}),
    },
    body: JSON.stringify(body),
  }).then((created) => {
    invalidateProjects();
    return created;
  });
}

/** PATCH /v1/project/{slug} — update one project. */
export function patchProject(
  slug: string,
  body: ProjectPatchBody,
  locale?: string,
): Promise<ApiProject> {
  return request<ApiProject>(`/v1/project/${encodeURIComponent(slug)}`, {
    method: "PATCH",
    headers: {
      ...(locale ? { "Accept-Language": locale } : {}),
    },
    body: JSON.stringify(body),
  }).then((updated) => {
    invalidateProjects();
    return updated;
  });
}

/** Generation row in POST /v1/project/guest-sync. */
export interface GuestStateSyncGenerationItem {
  /** Public generation slug from POST /v1/tool/{slug}/run. */
  slug: string;
  /** User-facing title from the guest localStorage bag. */
  title: string;
}

/** One project in POST /v1/project/guest-sync. */
export interface GuestStateSyncProjectItem {
  /** When true, overwrites the server project with slug `default`. */
  isDefault: boolean;
  name: string;
  description?: string | null;
  notes?: string | null;
  generations: GuestStateSyncGenerationItem[];
}

/** Body for POST /v1/project/guest-sync. */
export interface GuestStateSyncRequest {
  projects: GuestStateSyncProjectItem[];
}

/** Per-project result from POST /v1/project/guest-sync. */
export interface GuestStateSyncProjectResult {
  slug: string;
  name: string;
  isDefault: boolean;
  generationSlugs: string[];
}

/** `data` from POST /v1/project/guest-sync. */
export interface GuestStateSyncData {
  projectsSynced: number;
  generationsClaimed: number;
  projects: GuestStateSyncProjectResult[];
}

/**
 * POST /v1/project/guest-sync — claim guest localStorage projects/generations
 * for the authenticated user (after register, login, or guest checkout JWT).
 */
export function syncGuestProjects(
  body: GuestStateSyncRequest,
): Promise<GuestStateSyncData> {
  return request<GuestStateSyncData>("/v1/project/guest-sync", {
    method: "POST",
    body: JSON.stringify(body),
  }).then((data) => {
    // Claims guest generations into new projects — both lists change.
    invalidateGenerationsAndProjects();
    return data;
  });
}
